// ========================================
// SINCRONIZZAZIONE CLIENT-SIDE OTTIMIZZATA
// src/services/clientSideRapportiniSync.js
// ✅ FIX: Logica skip corretta (solo persona specifica, non tutto il rapportino)
// ✅ FIX: Non salta rapportini con ore parziali
// ========================================

import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  getWorkHoursForDate, 
  calculatePersonaleCost 
} from './workHoursIntegrationService';

/**
 * 🔄 VERSIONE AGGIORNATA: Sincronizza i rapportini con il sistema automatico
 */
export const syncRapportiniAfterWorkHoursChange = async (userId, date) => {
  try {
    console.log(`🔄 [CLIENT] Sincronizzazione rapportini per user ${userId} data ${date}`);
    
    // 1. Sincronizzazione normale
    const syncResult = await performNormalSync(userId, date);
    
    // 2. Trigger distribuzione automatica
    try {
      const { triggerAfterWorkHoursChange } = await import('./proportionalHoursDistribution');
      const distributionResult = await triggerAfterWorkHoursChange(userId, date);
      
      if (distributionResult.success) {
        console.log(`✅ [CLIENT] Sincronizzazione + distribuzione completata:`, distributionResult);
        return {
          syncSuccess: true,
          distributionSuccess: true,
          syncResult: syncResult,
          distributionResult: distributionResult
        };
      } else {
        console.log(`ℹ️ [CLIENT] Distribuzione non necessaria:`, distributionResult.message);
        return {
          syncSuccess: true,
          distributionSuccess: true,
          syncResult: syncResult,
          distributionResult: distributionResult
        };
      }
    } catch (error) {
      console.error('❌ [CLIENT] Errore nella distribuzione automatica:', error);
      return {
        syncSuccess: true,
        distributionSuccess: false,
        syncResult: syncResult,
        distributionError: error.message
      };
    }
    
  } catch (error) {
    console.error('❌ [CLIENT] Errore sincronizzazione completa:', error);
    return {
      syncSuccess: false,
      distributionSuccess: false,
      error: error.message
    };
  }
};

/**
 * 🔄 Sincronizzazione normale (senza distribuzione automatica)
 */
const performNormalSync = async (userId, date) => {
  try {
    // Trova tutti i rapportini che contengono questo utente per questa data
    const rapportiniQuery = query(
      collection(db, "rapportini"),
      where("data", "==", new Date(date)),
      where("stato", "==", "Attivo")
    );
    
    const rapportiniSnapshot = await getDocs(rapportiniQuery);
    const rapportiniToUpdate = [];
    
    // Filtra rapportini che contengono l'utente
    rapportiniSnapshot.forEach(doc => {
      const rapportino = doc.data();
      const hasUser = rapportino.personalePresente?.some(p => p.personaId === userId);
      
      if (hasUser) {
        rapportiniToUpdate.push({
          id: doc.id,
          ...rapportino
        });
      }
    });
    
    console.log(`📋 [CLIENT] Trovati ${rapportiniToUpdate.length} rapportini da sincronizzare`);
    
    // Sincronizza ogni rapportino
    const updatePromises = rapportiniToUpdate.map(async (rapportino) => {
      // ✅ FIX: Verifica se QUESTO SPECIFICO UTENTE deve essere sincronizzato
      if (shouldSkipPersonaSync(rapportino, userId)) {
        console.log(`⏭️ [CLIENT] Skip sincronizzazione per ${userId} nel rapportino ${rapportino.id}`);
        return { skipped: true, rapportinoId: rapportino.id };
      }
      
      return await updatePersonaleInRapportino(rapportino, userId, date);
    });
    
    const results = await Promise.all(updatePromises);
    
    const updated = results.filter(r => r && !r.skipped).length;
    const skipped = results.filter(r => r && r.skipped).length;
    
    console.log(`✅ [CLIENT] Sincronizzazione completata: ${updated} aggiornati, ${skipped} saltati`);
    
    return {
      success: true,
      updated,
      skipped,
      total: rapportiniToUpdate.length
    };
    
  } catch (error) {
    console.error('❌ [CLIENT] Errore sincronizzazione normale:', error);
    throw error;
  }
};

/**
 * ✅ FIX: Verifica se QUESTA PERSONA SPECIFICA deve essere saltata
 * Non salta tutto il rapportino, solo la persona specifica
 */
const shouldSkipPersonaSync = (rapportinoData, userId) => {
  const persona = rapportinoData.personalePresente?.find(p => p.personaId === userId);
  
  if (!persona) {
    console.log(`🚫 [CLIENT] Persona non trovata nel rapportino`);
    return true;
  }
  
  // ✅ FIX: Salta SOLO se questa persona specifica ha:
  // 1. Flag distributedHours = true
  // 2. E ore già > 0
  if (persona.distributedHours === true && 
      (persona.oreStandard > 0 || persona.oreStraordinario > 0)) {
    console.log(`🚫 [CLIENT] Persona ha già ore distribuite (${persona.oreStandard}h + ${persona.oreStraordinario}h)`);
    return true;
  }
  
  // ✅ FIX: Non saltare se le ore sono a zero (anche con distributedHours = true)
  // Questo permette di ri-sincronizzare persone che erano a zero
  if (persona.oreStandard === 0 && persona.oreStraordinario === 0) {
    console.log(`✅ [CLIENT] Persona ha ore = 0, procedo con sincronizzazione`);
    return false;
  }
  
  return false;
};

/**
 * 🔄 Aggiorna il personale in un rapportino specifico
 */
const updatePersonaleInRapportino = async (rapportino, userId, date) => {
  try {
    const persona = rapportino.personalePresente.find(p => p.personaId === userId);
    
    if (!persona) {
      return { skipped: true, reason: 'Persona non trovata' };
    }
    
    // ✅ FIX: Leggi SEMPRE dal workHours, ignora distribuzione
    // Questo permette di leggere ore inserite manualmente
    const hoursData = await getWorkHoursForDate(userId, date, false, null);
    
    if (!hoursData) {
      console.log(`ℹ️ [CLIENT] Nessuna ora trovata in workHours per ${userId} del ${date}`);
      return { skipped: true, reason: 'Nessuna ora in workHours' };
    }
    
    // Aggiorna la persona con le nuove ore
    const updatedPersona = await updatePersonaWithHours(persona, hoursData);
    
    // Aggiorna il documento rapportino
    const rapportinoRef = doc(db, 'rapportini', rapportino.id);
    
    const updatedPersonalePresente = rapportino.personalePresente.map(p => 
      p.personaId === userId ? updatedPersona : p
    );
    
    await updateDoc(rapportinoRef, {
      personalePresente: updatedPersonalePresente,
      lastSyncedAt: serverTimestamp()
    });
    
    console.log(`✅ [CLIENT] Rapportino ${rapportino.id} aggiornato per ${userId}`);
    
    return {
      success: true,
      rapportinoId: rapportino.id,
      personaId: userId,
      hours: {
        standard: updatedPersona.oreStandard,
        overtime: updatedPersona.oreStraordinario,
        total: updatedPersona.oreLavorate
      }
    };
    
  } catch (error) {
    console.error(`❌ [CLIENT] Errore aggiornamento rapportino ${rapportino.id}:`, error);
    throw error;
  }
};

/**
 * 🔧 Aggiorna i dati della persona con le ore da workHours
 */
const updatePersonaWithHours = async (persona, hoursData) => {
  try {
    const costoOrario = parseFloat(persona.costoOrario) || 0;
    
    // Caso speciale: Lettere (M, P, A, ecc.)
    if (hoursData.isSpecialCode) {
      return {
        ...persona,
        oreLavorate: 0,
        oreStandard: 0,
        oreStraordinario: 0,
        totale: 0,
        statoPresenza: 'speciale',
        notePersonale: `${hoursData.specialDescription} (client-sync)`,
        lastUpdated: new Date().toISOString(),
        syncedFromWorkHours: true
      };
    }
    
    // Giorno lavorativo normale
    const calculation = calculatePersonaleCost(
      hoursData.oreStandard,
      hoursData.oreStraordinario,
      costoOrario
    );
    
    let notePersonale = `${calculation.oreStandard}h standard`;
    if (calculation.oreStraordinario > 0) {
      notePersonale += ` + ${calculation.oreStraordinario}h straordinario`;
    }
    notePersonale += ' (client-sync)';
    
    return {
      ...persona,
      oreLavorate: calculation.oreTotali,
      oreStandard: calculation.oreStandard,
      oreStraordinario: calculation.oreStraordinario,
      totale: calculation.costoTotale,
      statoPresenza: 'presente',
      notePersonale: notePersonale,
      lastUpdated: new Date().toISOString(),
      syncedFromWorkHours: true
    };
    
  } catch (error) {
    console.error('❌ [CLIENT] Errore aggiornamento persona:', error);
    return persona;
  }
};

/**
 * 🔧 Verifica se un rapportino dovrebbe essere saltato dalla sincronizzazione
 * ⚠️ DEPRECATO - Usare shouldSkipPersonaSync invece
 */
export const shouldSkipClientSync = (rapportinoData, userId) => {
  // Usa la nuova logica che controlla solo la persona specifica
  return shouldSkipPersonaSync(rapportinoData, userId);
};

/**
 * ⚙️ CONFIGURAZIONE SINCRONIZZAZIONE
 */
export const syncConfig = {
  enableAutoDistribution: true,  // Abilita distribuzione automatica
  skipDistributedHours: true,    // Salta ore già distribuite (solo se > 0)
  logVerbose: true               // Log dettagliato
};

/**
 * 🔄 Forza sincronizzazione (ignora controlli skip)
 */
export const forceSyncRapportini = async (userId, date) => {
  const originalConfig = { ...syncConfig };
  syncConfig.skipDistributedHours = false;
  
  try {
    return await syncRapportiniAfterWorkHoursChange(userId, date);
  } finally {
    Object.assign(syncConfig, originalConfig);
  }
};